import { useCssVars as _useCssVars, defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, createVNode as _createVNode, createElementBlock as _createElementBlock, mergeProps as _mergeProps, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = ["aria-labelledby"]
const _hoisted_2 = { class: "content" }
const _hoisted_3 = ["tabindex", "role", "aria-labelledby"]

import {
  computed, onBeforeUnmount, onMounted, ref, useSlots
} from 'vue';
import { useStore } from 'vuex';
import { useI18n } from '@shell/composables/useI18n';
import { DEFAULT_FOCUS_TRAP_OPTS, getFirstFocusableElement, useBasicSetupFocusTrap } from '@shell/composables/focusTrap';
import { generateRandomAlphaString } from '@shell/utils/string';
import RcButton from '@components/RcButton/RcButton.vue';
import { RcHeading } from '@components/RcHeading';
import { widthFor, type RcModalProps } from './types';


export default /*@__PURE__*/_defineComponent({
  ...{ inheritAttrs: false },
  __name: 'RcModalDialog',
  props: {
    title: {},
    size: {},
    clickToClose: { type: Boolean }
  },
  emits: ["open", "close", "cancel", "primary-action"],
  setup(__props: any, { emit: __emit }) {

_useCssVars(_ctx => ({
  "d0a7141a": (width.value)
}))



const props = __props;

const emit = __emit;

const slots = useSlots();

const store = useStore();
const { t } = useI18n(store);

const uid = generateRandomAlphaString(12);
const dialogId = `rc-modal-${ uid }`;
const titleId = `rc-modal-title-${ uid }`;

const width = computed(() => widthFor(props.size));

const hasTitle = computed(() => !!props.title || !!slots.title);

const hasActions = computed(() => !!slots.actions || !!slots['primary-action']);

const dialog = ref<HTMLElement | null>(null);
const body = ref<HTMLElement | null>(null);
const actions = ref<HTMLElement | null>(null);

const bodyScrolls = ref(false);

const measureBody = () => {
  bodyScrolls.value = !!body.value && body.value.scrollHeight > body.value.clientHeight;
};

const bodyIsNamedRegion = computed(() => bodyScrolls.value && hasTitle.value);

function firstControlIn(region: HTMLElement | null): HTMLElement | null {
  const first = region && getFirstFocusableElement(region);

  return first && first !== document.body ? first : null;
}

function autofocusTarget(): HTMLElement | null {
  const marked = dialog.value?.querySelector<HTMLElement>('[autofocus]');

  if (!marked) {
    return null;
  }

  return marked.tabIndex >= 0 || marked.hasAttribute('tabindex') ? marked : firstControlIn(marked);
}

function initialFocus(): HTMLElement {
  return (autofocusTarget() || firstControlIn(body.value) || (bodyScrolls.value ? body.value : null) || firstControlIn(actions.value) || body.value || dialog.value) as HTMLElement;
}

let bodyResize: ResizeObserver | undefined;

onMounted(() => {
  emit('open');

  measureBody();

  if (body.value && typeof ResizeObserver !== 'undefined') {
    bodyResize = new ResizeObserver(measureBody);
    bodyResize.observe(body.value);
  }
});

onBeforeUnmount(() => bodyResize?.disconnect());

useBasicSetupFocusTrap(`#${ dialogId }`, {
  ...DEFAULT_FOCUS_TRAP_OPTS,
  escapeDeactivates: false,
  fallbackFocus:     `#${ dialogId }`,
  initialFocus,
});

function close() {
  emit('close');
}

function cancel() {
  emit('cancel');
  close();
}

function primaryAction() {
  emit('primary-action');
}

const slotContext = {
  close,
  cancel,
  primaryAction,
};

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    event.stopPropagation();
    cancel();
  }
}

function onBackgroundClick() {
  if (props.clickToClose) {
    cancel();
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "rc-modal-overlay",
    onMousedown: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["self","prevent"])),
    onClick: _withModifiers(onBackgroundClick, ["self"])
  }, [
    _createElementVNode("div", _mergeProps({
      id: dialogId,
      ref_key: "dialog",
      ref: dialog
    }, _ctx.$attrs, {
      class: "rc-modal",
      role: "dialog",
      "aria-modal": "true",
      "aria-labelledby": hasTitle.value ? titleId : undefined,
      tabindex: "-1",
      onKeydown: onKeydown
    }), [
      _createElementVNode("div", _hoisted_2, [
        (hasTitle.value)
          ? (_openBlock(), _createBlock(_unref(RcHeading), {
              key: 0,
              id: titleId,
              size: 3,
              class: "title",
              "data-testid": "rc-modal-title"
            }, {
              default: _withCtx(() => [
                _renderSlot(_ctx.$slots, "title", {}, () => [
                  _createTextVNode(_toDisplayString(__props.title), 1)
                ])
              ]),
              _: 3
            }))
          : _createCommentVNode("", true),
        _cache[1] || (_cache[1] = _createTextVNode()),
        _createElementVNode("div", {
          ref_key: "body",
          ref: body,
          class: "body",
          tabindex: bodyScrolls.value ? 0 : undefined,
          role: bodyIsNamedRegion.value ? 'region' : undefined,
          "aria-labelledby": bodyIsNamedRegion.value ? titleId : undefined,
          "data-testid": "rc-modal-body"
        }, [
          _renderSlot(_ctx.$slots, "default")
        ], 8, _hoisted_3)
      ]),
      _cache[3] || (_cache[3] = _createTextVNode()),
      (hasActions.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            ref_key: "actions",
            ref: actions,
            class: "actions",
            "data-testid": "rc-modal-actions"
          }, [
            _renderSlot(_ctx.$slots, "actions", _normalizeProps(_guardReactiveProps(slotContext)), () => [
              _createVNode(RcButton, {
                variant: "tertiary",
                size: "large",
                "data-testid": "rc-modal-cancel",
                onClick: cancel
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('generic.cancel')), 1)
                ]),
                _: 1
              }),
              _cache[2] || (_cache[2] = _createTextVNode()),
              _renderSlot(_ctx.$slots, "primary-action", _normalizeProps(_guardReactiveProps(slotContext)))
            ])
          ], 512))
        : _createCommentVNode("", true)
    ], 16, _hoisted_1)
  ], 32))
}
}

})